import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getSeasonLeaderboard, getRaces, getCurrentDrivers, getNews } from '../api';
import { getStoredUser } from '../user';
import NewsTicker from '../components/NewsTicker';

const PODIUM = ['#FFD700', '#C0C0C0', '#CD7F32'];

const kf = `
  @keyframes driverSpot {
    0%, 100% { transform: scale(0.76); filter: grayscale(55%) brightness(0.5); }
    50%       { transform: scale(1.08); filter: grayscale(0%)  brightness(1.1); }
  }
  @keyframes goldShimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes countPulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.55; }
  }
  @keyframes rowIn {
    from { opacity: 0; transform: translateX(-4px); }
    to   { opacity: 1; transform: translateX(0); }
  }
`;

// ── Countdown hook ─────────────────────────────────────────────────────────────
function useCountdown(targetDate) {
  const [t, setT] = useState(null);
  useEffect(() => {
    if (!targetDate) return;
    const tick = () => {
      const ms = new Date(targetDate) - Date.now();
      if (ms <= 0) { setT(null); return; }
      setT({
        d: Math.floor(ms / 86_400_000),
        h: Math.floor((ms % 86_400_000) / 3_600_000),
        m: Math.floor((ms % 3_600_000) / 60_000),
        s: Math.floor((ms % 60_000) / 1_000),
        imminent: ms < 86_400_000,
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  return t;
}

// ── Countdown pills ────────────────────────────────────────────────────────────
function Countdown({ targetDate, compact }) {
  const t = useCountdown(targetDate);
  if (!t) return null;

  const units = t.d > 0
    ? [['d', t.d], ['h', t.h], ['m', t.m]]
    : [['h', t.h], ['m', t.m], ['s', t.s]];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: compact ? 0 : 24 }}>
      {!compact && (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: '0.12em', marginRight: 2 }}>
          Race in
        </span>
      )}
      {units.map(([unit, val]) => (
        <span key={unit} style={{
          fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: compact ? 12 : 14,
          color: t.imminent ? '#f9b000' : '#888',
          background: '#0d0d0d', border: `1px solid ${t.imminent ? 'rgba(249,176,0,0.3)' : '#1e1e1e'}`,
          borderRadius: 4, padding: compact ? '3px 7px' : '4px 10px',
          animation: t.imminent ? 'countPulse 1.5s ease-in-out infinite' : 'none',
        }}>
          {String(val).padStart(2, '0')}
          <span style={{ fontWeight: 400, fontSize: compact ? 9 : 11, color: '#444', marginLeft: 2 }}>{unit}</span>
        </span>
      ))}
    </div>
  );
}

// ── Next race card — hero for both pre-season and in-season ────────────────────
function NextRaceCard({ race, drivers, hasScores }) {
  // useMemo must be called before any early return
  const headshotDrivers = React.useMemo(() => {
    const withShots = drivers.filter(d => d.headshotUrl);
    return [...withShots].sort(() => Math.random() - 0.5).slice(0, 8);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drivers.length]);

  if (!race) return null;

  const dateStr = race.race_date
    ? new Date(race.race_date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
    : null;

  const dur = 18;

  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      background: '#111', borderRadius: 8,
      borderLeft: '4px solid #f9b000',
      padding: hasScores ? '20px 24px' : '24px 28px',
      marginBottom: 28,
      animation: 'fadeUp 0.4s ease both',
    }}>
      {/* Background headshots — decorative texture on right edge (in-season only) */}
      {hasScores && headshotDrivers.length > 0 && (
        <div style={{
          position: 'absolute', top: 0, right: 0, bottom: 0,
          display: 'flex', alignItems: 'center', gap: 10,
          paddingRight: 20,
          maskImage: 'linear-gradient(to left, rgba(0,0,0,0.18) 0%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,0.18) 0%, transparent 100%)',
          pointerEvents: 'none',
        }}>
          {headshotDrivers.map((d, i) => (
            <img
              key={d.acronym}
              src={d.headshotUrl}
              alt=""
              style={{
                width: 52, height: 52, borderRadius: '50%',
                objectFit: 'cover', flexShrink: 0, opacity: 0.22,
                border: `2px solid ${d.teamColour ? '#' + d.teamColour : '#333'}`,
                animation: `driverSpot ${dur}s ease-in-out infinite`,
                animationDelay: `${-(i / headshotDrivers.length) * dur}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Card content */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24 }}>

        {/* Left: race info */}
        <div style={{ minWidth: 0 }}>
          <p style={{
            fontFamily: 'var(--font-mono)', fontSize: 10, color: '#f9b000',
            letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 6,
          }}>
            Next Race · Round {race.round}
          </p>
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: hasScores ? 24 : 28,
            fontWeight: 700, textTransform: 'uppercase', color: '#f0f0f0',
            lineHeight: 1, marginBottom: 6,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {race.name}
          </div>
          {dateStr && (
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#555', marginBottom: hasScores ? 0 : 14 }}>
              {dateStr}
            </div>
          )}
          {/* Hint text for new/returning users — shown in pre-season full card */}
          {!hasScores && (
            <p style={{ color: '#555', fontSize: 13, lineHeight: 1.6, marginTop: 10, maxWidth: 380 }}>
              Pick your top 10 finishers before the lights go out. Nail the exact position and score F1 points. Best total across the season wins.
            </p>
          )}
          {/* Compact countdown below date (in-season) */}
          {hasScores && race.race_date && (
            <div style={{ marginTop: 10 }}>
              <Countdown targetDate={race.race_date} compact />
            </div>
          )}
        </div>

        {/* Right: CTA + countdown (pre-season) */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12, flexShrink: 0 }}>
          {!hasScores && <Countdown targetDate={race.race_date} compact={false} />}
          <Link to={`/races/${race.id}`} style={{
            display: 'inline-block', background: '#f9b000', color: '#111',
            fontFamily: 'var(--font-display)', fontWeight: 700,
            fontSize: hasScores ? 13 : 15,
            textTransform: 'uppercase', letterSpacing: '0.06em',
            padding: hasScores ? '9px 20px' : '11px 26px',
            borderRadius: 4, textDecoration: 'none', whiteSpace: 'nowrap',
          }}>
            Predict →
          </Link>
        </div>

      </div>
    </div>
  );
}

// ── Race dot pips ──────────────────────────────────────────────────────────────
function RacePips({ scored, total }) {
  if (!total) return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#555' }}>{scored}</span>;
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 100 }}>
      {Array.from({ length: total }, (_, i) => (
        <span key={i} style={{
          width: 6, height: 6, borderRadius: '50%',
          background: i < scored ? '#f9b000' : '#1e1e1e',
          display: 'inline-block', flexShrink: 0,
          border: i < scored ? 'none' : '1px solid #2a2a2a',
        }} />
      ))}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────
export default function HomePage() {
  const [rows, setRows]                         = useState([]);
  const [nextRace, setNextRace]                 = useState(null);
  const [racesWithResults, setRacesWithResults] = useState(0);
  const [drivers, setDrivers]                   = useState([]);
  const [news, setNews]                         = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState(null);

  useEffect(() => {
    Promise.all([
      getSeasonLeaderboard(),
      getRaces(),
      getCurrentDrivers().catch(() => []),
    ]).then(([lb, races, driverList]) => {
      setRows(lb);
      const now  = Date.now();
      const next = races.find(r => !r.locked && !r.cancelled && r.race_date && new Date(r.race_date) > now);
      setNextRace(next ?? null);
      setRacesWithResults(races.filter(r => r.official_top10).length);
      setDrivers(driverList);
    }).catch(e => setError(e.message))
      .finally(() => setLoading(false));

    getNews().then(setNews).catch(() => {});
  }, []);

  const hasScores   = rows.length > 0;
  const currentUser = getStoredUser();
  const year        = new Date().getFullYear();

  return (
    <div>
      <style>{kf}</style>

      {/* ── PRE-SEASON HERO TEXT (no scores yet) ── */}
      {!hasScores && !loading && (
        <div style={{ marginBottom: 8, animation: 'fadeUp 0.45s ease both' }}>
          <p style={{
            fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
            color: '#e8002d', letterSpacing: '0.18em', textTransform: 'uppercase',
            marginBottom: 4,
          }}>
            Bryggeriet Ångkvarn presents
          </p>
          <p style={{
            fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
            color: '#444', letterSpacing: '0.16em', textTransform: 'uppercase',
            marginBottom: 12,
          }}>
            {year} Season · Prediction Pool
          </p>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 64, fontWeight: 800,
            textTransform: 'uppercase', letterSpacing: '0.01em', lineHeight: 0.95,
            color: '#f0f0f0', marginBottom: 18,
          }}>
            Who takes the chequered flag?
          </h1>
        </div>
      )}

      {/* ── NEXT RACE CARD (hero for both states) ── */}
      {!loading && nextRace && (
        <NextRaceCard race={nextRace} drivers={drivers} hasScores={hasScores} />
      )}

      {/* ── NEWS TICKER ── */}
      {news.length > 0 && <NewsTicker headlines={news} />}

      {/* ── STANDINGS HEADER ── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, color: '#e8002d', letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 4 }}>
            {year} Season
          </p>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.02em', lineHeight: 1,
            color: '#f0f0f0', margin: 0,
          }}>
            Standings
          </h2>
        </div>
        {hasScores && racesWithResults > 0 && (
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#333', letterSpacing: '0.08em', paddingBottom: 2 }}>
            AFTER ROUND {racesWithResults}
          </p>
        )}
      </div>

      {/* ── STANDINGS BODY ── */}
      {loading && <p style={{ color: '#666' }}>Loading...</p>}
      {error   && <p style={{ color: '#f9b000' }}>Error: {error}</p>}

      {!loading && !error && !hasScores && (
        <div style={{
          background: '#111', borderRadius: 6, border: '1px solid #1a1a1a',
          padding: '22px 20px', textAlign: 'center',
        }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: '#333', letterSpacing: '0.06em' }}>
            Standings appear after the first race result is posted.
          </p>
        </div>
      )}

      {!loading && !error && hasScores && (
        <div style={{ background: '#111', borderRadius: 8, overflow: 'hidden', border: '1px solid #161616' }}>
          {rows.map((row, i) => {
            const isLeader    = i === 0;
            const isMe        = !!currentUser && row.user_name === currentUser;
            const score       = parseInt(row.total_score ?? row.score ?? 0, 10);
            const leaderScore = parseInt(rows[0]?.total_score ?? rows[0]?.score ?? 1, 10);
            const gap         = i === 1 && leaderScore > score ? leaderScore - score : null;
            const barPct      = leaderScore > 0 ? (score / leaderScore) * 100 : 0;
            const rankColor   = PODIUM[i] ?? (isMe ? 'rgba(249,176,0,0.7)' : '#252525');

            return (
              <div
                key={row.user_name}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 16px',
                  borderBottom: i < rows.length - 1 ? '1px solid #0d0d0d' : 'none',
                  borderLeft: `2px solid ${isLeader ? 'rgba(249,176,0,0.3)' : isMe ? 'rgba(249,176,0,0.15)' : 'transparent'}`,
                  background: isLeader ? 'rgba(249,176,0,0.04)' : isMe ? 'rgba(249,176,0,0.02)' : 'transparent',
                  transition: 'background 0.12s, transform 0.12s',
                  animation: 'rowIn 0.35s ease both',
                  animationDelay: `${i * 40}ms`,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = isLeader ? 'rgba(249,176,0,0.07)' : '#131313';
                  e.currentTarget.style.transform  = 'translateX(3px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = isLeader ? 'rgba(249,176,0,0.04)' : isMe ? 'rgba(249,176,0,0.02)' : 'transparent';
                  e.currentTarget.style.transform  = 'translateX(0)';
                }}
              >
                {/* Rank */}
                <span style={{
                  fontFamily: 'var(--font-display)', fontWeight: 800,
                  fontSize: 24, lineHeight: 1,
                  width: 34, textAlign: 'center', flexShrink: 0,
                  ...(isLeader ? {
                    background: 'linear-gradient(90deg, #b8860b 0%, #FFD700 35%, #fffbe6 50%, #FFD700 65%, #b8860b 100%)',
                    backgroundSize: '200% auto',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    animation: 'goldShimmer 4s linear infinite',
                  } : { color: rankColor }),
                }}>
                  {i + 1}
                </span>

                {/* Name + score bar */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1,
                    color: isLeader || isMe ? '#fff' : '#aaa',
                    marginBottom: 5,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {row.user_name}{isMe && ' ★'}
                  </div>
                  <div style={{ height: 3, background: '#181818', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 2,
                      width: `${barPct}%`,
                      background: isLeader ? '#f9b000' : isMe ? 'rgba(249,176,0,0.4)' : '#272727',
                      transition: 'width 0.9s ease',
                    }} />
                  </div>
                  {gap !== null && (
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#383838', marginTop: 3 }}>
                      △ +{gap} pts from leader
                    </div>
                  )}
                </div>

                {/* Race pips */}
                {racesWithResults > 0 && (
                  <RacePips
                    scored={parseInt(row.races_scored ?? 0, 10)}
                    total={racesWithResults}
                  />
                )}

                {/* Points */}
                <span style={{
                  fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 700,
                  color: isLeader ? '#f9b000' : isMe ? 'rgba(249,176,0,0.75)' : '#555',
                  flexShrink: 0, minWidth: 46, textAlign: 'right',
                }}>
                  {score}
                  <span style={{ fontSize: 10, color: '#333', marginLeft: 3 }}>pts</span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
